import React, { useState } from 'react';

const legalPages = {
  '/privacy-policy': {
    title: 'Privacy Policy',
    content: 'Founder Neural OS is committed to protecting founder, employee, and organizational data. All behavioral assessments, AI-driven diagnostics, and strategic conversations are handled with confidentiality and secure processing standards.',
  },
  '/terms-of-service': {
    title: 'Terms of Service',
    content: 'All engagements are governed by strategic consulting, media usage, and organizational intelligence delivery agreements. Founder Neural OS retains the right to structure strategic deliverables as defined in signed engagement contracts.',
  },
  '/ai-ethics-policy': {
    title: 'AI Ethics Policy',
    content: 'Founder Neural OS follows responsible AI usage standards for workforce intelligence, behavioral assessments, mentoring systems, and organizational diagnostics while prioritizing fairness, privacy, and ethical deployment.',
  },
  '/content-usage-policy': {
    title: 'Content Usage Policy',
    content: 'All founder interviews, documentary footage, strategic media assets, and organizational communication materials are governed through approved content licensing and publishing frameworks.',
  },
};

const CONTACT_EMAIL = 'contact@founderneuralos.com';
const mailto = (subject) => 'mailto:' + CONTACT_EMAIL + '?subject=' + encodeURIComponent(subject);

export default function FounderNeuralOS() {
  const [currentPage, setCurrentPage] = useState('/');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  if (legalPages[currentPage]) {
    return (
      <div className="min-h-screen bg-black text-white px-8 md:px-20 py-24 font-sans">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => setCurrentPage('/')} className="text-blue-400 hover:underline inline-block mb-10">
            ← Back to Founder Neural OS
          </button>

          <h1 className="text-5xl font-bold mb-10">
            {legalPages[currentPage].title}
          </h1>

          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-10 text-zinc-300 leading-relaxed text-lg">
            {legalPages[currentPage].content}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-sans">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-2xl bg-black/70 border-b border-cyan-500/10 shadow-[0_0_40px_rgba(34,211,238,0.06)]">
        <div className="max-w-7xl mx-auto px-6 md:px-16 py-5 flex items-center justify-between">
          <button onClick={() => setCurrentPage('/')} className="flex items-center gap-4">
            <img
              src="/founder-neural-os-logo.png"
              alt="Founder Neural OS"
              className="h-24 w-auto object-contain drop-shadow-[0_0_20px_rgba(34,211,238,0.35)]"
            />
          </button>

          <nav className="hidden md:flex items-center gap-8 text-zinc-400 text-sm font-medium">
            <a href="#ecosystem" className="hover:text-cyan-300 transition duration-300">Ecosystem</a>
            <a href="#services" className="hover:text-cyan-300 transition duration-300">Services</a>
            <a href="#workflow" className="hover:text-cyan-300 transition duration-300">Workflow</a>
            <a href="#tracks" className="hover:text-cyan-300 transition duration-300">Engagement</a>
            <a href="#transformation" className="hover:text-cyan-300 transition duration-300">Impact</a>
          </nav>

          <a
            href={mailto('Founder Neural OS — Inquiry')}
            className="hidden md:inline-block bg-gradient-to-r from-pink-500 via-orange-400 to-cyan-400 text-black px-5 py-2 rounded-xl font-semibold hover:opacity-90 transition shadow-lg"
          >
            Contact Us
          </a>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile menu drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-cyan-500/10 bg-black/95 px-6 py-6">
            <nav className="flex flex-col gap-4 text-zinc-300">
              <a href="#ecosystem" onClick={() => setMobileMenuOpen(false)} className="hover:text-cyan-300">Ecosystem</a>
              <a href="#services" onClick={() => setMobileMenuOpen(false)} className="hover:text-cyan-300">Services</a>
              <a href="#workflow" onClick={() => setMobileMenuOpen(false)} className="hover:text-cyan-300">Workflow</a>
              <a href="#tracks" onClick={() => setMobileMenuOpen(false)} className="hover:text-cyan-300">Engagement</a>
              <a href="#transformation" onClick={() => setMobileMenuOpen(false)} className="hover:text-cyan-300">Impact</a>
              <a href={mailto('Founder Neural OS — Inquiry')} onClick={() => setMobileMenuOpen(false)} className="mt-2 bg-gradient-to-r from-pink-500 via-orange-400 to-cyan-400 text-black px-5 py-2 rounded-xl font-semibold text-center">Contact Us</a>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden px-6 md:px-16 py-28 border-b border-cyan-500/10 bg-[radial-gradient(circle_at_top_left,_rgba(236,72,153,0.18),_transparent_25%),radial-gradient(circle_at_top_right,_rgba(34,211,238,0.18),_transparent_25%),radial-gradient(circle_at_bottom,_rgba(249,115,22,0.15),_transparent_30%)]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
          <div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6 text-xs uppercase tracking-widest text-zinc-400">
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-400"></span>Intelligent Founders</span>
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-cyan-400"></span>Strong Organisations</span>
              <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-pink-400"></span>Lasting Legacy</span>
            </div>

            <h1 className="text-6xl md:text-8xl font-black leading-[0.95] tracking-tight bg-gradient-to-r from-pink-500 via-orange-300 to-cyan-300 bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(236,72,153,0.25)]">
              Turn Your Story.
              <br />
              Unlock Your
              <br />
              Organization's Future.
            </h1>
            <p className="mt-6 text-xl text-zinc-300 leading-relaxed">
              Founder Neural OS is an AI-powered founder intelligence ecosystem that captures, decodes, and activates the founder DNA driving organizational growth, alignment, visibility, and long-term impact.
            </p>
            <p className="mt-6 text-zinc-400 text-lg leading-relaxed">
              Through founder interviews, neural mapping, AI workforce mentoring, strategic podcasts, GTM storytelling, and organizational diagnostics, we help startups, SMEs, and enterprises scale with clarity and institutional intelligence.
            </p>

            <div className="mt-12 flex flex-wrap gap-5">
              <a
                href={mailto('Start DNA Assessment')}
                className="bg-gradient-to-r from-pink-500 via-orange-400 to-cyan-400 text-black px-6 py-3 rounded-2xl font-semibold hover:opacity-90 transition shadow-xl"
              >
                Start DNA Assessment
              </a>
              <a
                href={mailto('Book a Demo')}
                className="border border-cyan-500/30 bg-cyan-500/5 text-cyan-300 px-6 py-3 rounded-2xl hover:bg-cyan-500/10 transition"
              >
                Book a Demo
              </a>
            </div>
          </div>

          <div className="relative bg-gradient-to-br from-zinc-950 via-black to-zinc-950 border border-cyan-500/20 rounded-[2rem] p-10 shadow-[0_0_80px_rgba(34,211,238,0.12)] overflow-hidden">
            <svg viewBox="0 0 480 480" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto drop-shadow-[0_0_40px_rgba(34,211,238,0.25)]">
              <defs>
                <radialGradient id="fnoGlow1" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#ec4899" stopOpacity="0.35"/>
                  <stop offset="100%" stopColor="#ec4899" stopOpacity="0"/>
                </radialGradient>
                <radialGradient id="fnoGlow2" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.35"/>
                  <stop offset="100%" stopColor="#22d3ee" stopOpacity="0"/>
                </radialGradient>
              </defs>
              <circle cx="120" cy="240" r="110" fill="url(#fnoGlow1)"/>
              <circle cx="360" cy="240" r="110" fill="url(#fnoGlow2)"/>
              <g strokeWidth="1" opacity="0.5" fill="none">
                <line x1="100" y1="90" x2="240" y2="60" stroke="#ec4899"/>
                <line x1="100" y1="90" x2="240" y2="120" stroke="#f97316"/>
                <line x1="100" y1="180" x2="240" y2="60" stroke="#a855f7"/>
                <line x1="100" y1="180" x2="240" y2="180" stroke="#ec4899"/>
                <line x1="100" y1="180" x2="240" y2="270" stroke="#22d3ee"/>
                <line x1="100" y1="270" x2="240" y2="120" stroke="#10b981"/>
                <line x1="100" y1="270" x2="240" y2="270" stroke="#f97316"/>
                <line x1="100" y1="270" x2="240" y2="330" stroke="#ec4899"/>
                <line x1="100" y1="360" x2="240" y2="270" stroke="#a855f7"/>
                <line x1="100" y1="360" x2="240" y2="330" stroke="#22d3ee"/>
                <line x1="100" y1="360" x2="240" y2="390" stroke="#f97316"/>
                <line x1="240" y1="60" x2="380" y2="140" stroke="#ec4899"/>
                <line x1="240" y1="120" x2="380" y2="140" stroke="#f97316"/>
                <line x1="240" y1="120" x2="380" y2="240" stroke="#22d3ee"/>
                <line x1="240" y1="180" x2="380" y2="140" stroke="#a855f7"/>
                <line x1="240" y1="180" x2="380" y2="240" stroke="#10b981"/>
                <line x1="240" y1="270" x2="380" y2="240" stroke="#ec4899"/>
                <line x1="240" y1="270" x2="380" y2="340" stroke="#22d3ee"/>
                <line x1="240" y1="330" x2="380" y2="240" stroke="#f97316"/>
                <line x1="240" y1="330" x2="380" y2="340" stroke="#a855f7"/>
                <line x1="240" y1="390" x2="380" y2="340" stroke="#10b981"/>
              </g>
              <circle cx="100" cy="90" r="8" fill="#ec4899" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="100" cy="180" r="8" fill="#f97316" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="100" cy="270" r="8" fill="#22d3ee" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="100" cy="360" r="8" fill="#a855f7" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="240" cy="60" r="10" fill="#ec4899" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="240" cy="120" r="12" fill="#22d3ee" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="240" cy="180" r="14" fill="#f97316" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="240" cy="270" r="12" fill="#ec4899" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="240" cy="330" r="10" fill="#22d3ee" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="240" cy="390" r="10" fill="#a855f7" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="380" cy="140" r="12" fill="#f97316" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="380" cy="240" r="16" fill="#22d3ee" stroke="#fff" strokeWidth="0.5"/>
              <circle cx="380" cy="340" r="12" fill="#ec4899" stroke="#fff" strokeWidth="0.5"/>
              <text x="100" y="445" fill="#a1a1aa" fontSize="11" textAnchor="middle" fontFamily="sans-serif" letterSpacing="2">FOUNDER DNA</text>
              <text x="240" y="445" fill="#a1a1aa" fontSize="11" textAnchor="middle" fontFamily="sans-serif" letterSpacing="2">NEURAL OS</text>
              <text x="380" y="445" fill="#a1a1aa" fontSize="11" textAnchor="middle" fontFamily="sans-serif" letterSpacing="2">ORG IMPACT</text>
            </svg>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_rgba(34,211,238,0.18),_transparent_35%)] pointer-events-none"></div>
          </div>
        </div>

        {/* 5-tier strip — now correctly placed BELOW the grid */}
        <div className="mt-24 max-w-6xl mx-auto bg-gradient-to-r from-zinc-950 via-black to-zinc-950 border border-cyan-500/10 rounded-[2rem] p-10 grid grid-cols-2 md:grid-cols-5 gap-8 text-center shadow-[0_0_60px_rgba(34,211,238,0.06)]">
          {[
            'Startups',
            'SMEs',
            'Family Businesses',
            'Scaleups',
            'Enterprises',
          ].map((item, index) => (
            <div key={index} className="flex flex-col items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-pink-500 via-orange-400 to-cyan-400 opacity-90"></div>
              <p className="text-zinc-300 font-medium">{item}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Credibility Strip */}
      <section className="px-6 md:px-16 py-20 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-zinc-500 text-sm uppercase tracking-[0.3em] mb-12">
            Built On Decades of Institutional Intelligence
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { num: '32+', label: 'Years Cross-Industry Leadership' },
              { num: '3-Layer', label: 'Behavioral DNA Framework' },
              { num: 'AI-Native', label: 'Diagnostic & Mentoring Systems' },
              { num: 'Founder-First', label: 'Engagement Methodology' },
            ].map((stat, i) => (
              <div key={i} className="bg-gradient-to-br from-zinc-950 to-black border border-zinc-800 rounded-2xl p-6 text-center hover:border-cyan-500/30 transition">
                <div className="text-3xl font-bold bg-gradient-to-r from-pink-400 via-orange-300 to-cyan-300 bg-clip-text text-transparent">
                  {stat.num}
                </div>
                <p className="mt-2 text-zinc-400 text-sm leading-snug">{stat.label}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-zinc-500 text-sm mt-10 italic">
            Built by industry professionals with deep cross-sector experience across finance, advertising, and strategic consulting.
          </p>
        </div>
      </section>

      {/* Vision Section */}
      <section className="px-6 md:px-16 py-28">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-5xl font-black tracking-tight bg-gradient-to-r from-white via-cyan-200 to-pink-300 bg-clip-text text-transparent">
            The Future of Founder-Led Organizations
          </h2>
          <p className="mt-8 text-xl text-zinc-300 leading-relaxed">
            Founder Neural OS is designed to help companies align leadership, culture, communication, workforce intelligence, and market visibility into one connected operating ecosystem.
          </p>
          <p className="mt-6 text-lg text-zinc-400 leading-relaxed">
            We believe every organization inherits the psychology of its founders. Through strategic interviews, organizational diagnostics, AI mentoring systems, and content intelligence, we help companies identify operational drift, strengthen internal alignment, and build enduring institutional systems.
          </p>
        </div>
      </section>

      {/* Ecosystem — reframed as umbrella */}
      <section id="ecosystem" className="px-8 md:px-20 py-24 bg-gradient-to-b from-zinc-950 to-black border-y border-zinc-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-4">Products Within Founder Neural OS</h2>
          <p className="text-center text-zinc-400 max-w-3xl mx-auto leading-relaxed mb-16">
            Founder Neural OS is the umbrella intelligence layer. NiyamHR and Pettai are dedicated product systems built within this ecosystem, each addressing a specific organizational dimension.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-zinc-950 to-black border border-pink-500/10 rounded-3xl p-8 hover:border-cyan-500/20 transition">
              <div className="text-xs uppercase tracking-widest text-cyan-400 mb-3">Workforce Intelligence Layer</div>
              <h3 className="text-3xl font-semibold">NiyamHR</h3>
              <p className="mt-4 text-zinc-400 leading-relaxed">
                AI-powered employee mentoring and organizational alignment platform designed to improve workforce growth, behavioral intelligence, communication, and culture alignment.
              </p>
              <ul className="mt-6 space-y-3 text-zinc-300">
                <li>• Employee neural assessments</li>
                <li>• AI mentoring systems</li>
                <li>• Culture drift mapping</li>
                <li>• Leadership alignment diagnostics</li>
                <li>• Organizational intelligence insights</li>
              </ul>
              <a
                href="https://niyamhr.in"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-8 text-blue-400 hover:underline"
              >
                Visit NiyamHR →
              </a>
            </div>

            <div className="bg-gradient-to-br from-zinc-950 to-black border border-pink-500/10 rounded-3xl p-8 hover:border-cyan-500/20 transition">
              <div className="text-xs uppercase tracking-widest text-pink-400 mb-3">Brand & Visibility Layer</div>
              <h3 className="text-3xl font-semibold">Pettai App</h3>
              <p className="mt-4 text-zinc-400 leading-relaxed">
                AI-assisted founder content and strategic communication platform helping organizations amplify visibility through GTM storytelling, founder interviews, and function-driven content systems.
              </p>
              <ul className="mt-6 space-y-3 text-zinc-300">
                <li>• Founder-led content generation</li>
                <li>• GTM communication workflows</li>
                <li>• Product rollout storytelling</li>
                <li>• Strategic branding systems</li>
                <li>• YouTube & LinkedIn visibility engine</li>
              </ul>
              <a
                href="https://pettaiapp.in"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-8 text-blue-400 hover:underline"
              >
                Visit Pettai →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="px-8 md:px-20 py-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16">Founder Neural OS Services</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Founder Deep-Dive Interviews',
                desc: 'Strategic documentary-style conversations capturing founder psychology, leadership systems, operational philosophy, and organizational intelligence.',
              },
              {
                title: 'Neural Mapping & Diagnostics',
                desc: 'AI-assisted behavioral and organizational analysis identifying communication patterns, culture signals, operational bottlenecks, and leadership archetypes.',
              },
              {
                title: 'Strategic Media Architecture',
                desc: 'High-quality founder-led media systems designed for visibility, employer branding, GTM storytelling, investor trust, and customer engagement.',
              },
              {
                title: 'AI Employee Mentoring',
                desc: 'Continuous workforce alignment through AI-guided mentoring, behavioral feedback systems, and employee growth intelligence.',
              },
              {
                title: 'Organizational Alignment',
                desc: 'Systems designed to improve accountability, communication clarity, leadership consistency, and operational visibility across teams.',
              },
              {
                title: 'Content & Distribution Engine',
                desc: 'Podcast clips, founder narratives, YouTube growth systems, LinkedIn authority content, and strategic communication frameworks.',
              },
            ].map((service, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-zinc-900 to-black border border-zinc-800 rounded-3xl p-8 hover:border-cyan-400/40 transition hover:-translate-y-1 duration-300 shadow-xl"
              >
                <h3 className="text-2xl font-semibold">{service.title}</h3>
                <p className="mt-4 text-zinc-400 leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section id="workflow" className="px-8 md:px-20 py-24 bg-gradient-to-b from-zinc-950 to-black border-y border-zinc-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16">How It Works</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              'Founder Discovery & Strategic Assessment',
              'Deep-Dive Neural Interview Session',
              'AI Mapping & Organizational Intelligence Analysis',
              'Strategic Media + Team Alignment Rollout',
            ].map((step, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-zinc-950 to-black border border-pink-500/10 rounded-3xl p-8 hover:border-cyan-500/20 transition text-center"
              >
                <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-r from-pink-500 via-orange-400 to-cyan-400 text-black flex items-center justify-center font-bold text-xl mb-6 shadow-lg">
                  {index + 1}
                </div>
                <p className="text-zinc-300 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Tracks — pricing removed */}
      <section id="tracks" className="px-8 md:px-20 py-24">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-6">Engagement Tracks</h2>
          <p className="text-center text-zinc-400 max-w-3xl mx-auto leading-relaxed mb-16 text-lg">
            Three structured pathways for founder-led organizations to enter the Founder Neural OS ecosystem. Each track is scoped, sequenced, and priced based on organizational size, leadership complexity, and strategic objectives.
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Sprint */}
            <div className="bg-gradient-to-br from-zinc-950 via-black to-zinc-900 border border-cyan-500/10 rounded-[2rem] p-10 flex flex-col shadow-[0_0_50px_rgba(34,211,238,0.05)]">
              <div>
                <div className="text-xs uppercase tracking-widest text-cyan-400 mb-3">Entry Track</div>
                <h3 className="text-3xl font-semibold">Founder Signal Sprint</h3>
                <p className="mt-4 text-zinc-400 leading-relaxed">
                  A focused 21-day founder visibility and positioning engagement for emerging companies and founder-led brands.
                </p>
                <div className="mt-8 text-xl font-semibold text-zinc-300">
                  Investment: On Request
                </div>
                <ul className="mt-8 space-y-4 text-zinc-300">
                  <li>• 1 founder deep-dive interview (90 mins)</li>
                  <li>• 12 edited strategic video clips</li>
                  <li>• Founder neural snapshot report</li>
                  <li>• LinkedIn + YouTube publishing support</li>
                  <li>• Basic GTM storytelling consultation</li>
                  <li>• 21-day engagement cycle</li>
                </ul>
              </div>
              <a href={mailto('Founder Signal Sprint — Engagement Brief')} className="mt-10 border border-zinc-600 px-6 py-3 rounded-2xl hover:bg-zinc-800 transition text-center">
                Request Engagement Brief
              </a>
            </div>

            {/* Mid Tier */}
            <div className="bg-gradient-to-br from-cyan-300 via-white to-pink-200 text-black rounded-3xl p-8 flex flex-col shadow-[0_0_80px_rgba(34,211,238,0.25)] scale-105 border border-cyan-200">
              <div>
                <div className="inline-block bg-black text-white px-4 py-1 rounded-full text-sm font-medium mb-6">
                  Most Strategic
                </div>
                <h3 className="text-3xl font-semibold">Founder Neural OS</h3>
                <p className="mt-4 text-zinc-700 leading-relaxed">
                  A structured 60-day founder intelligence and organizational alignment engagement integrating strategic media, AI diagnostics, and workforce visibility systems.
                </p>
                <div className="mt-8 text-xl font-semibold text-zinc-900">
                  Investment: On Request
                </div>
                <ul className="mt-8 space-y-4 text-zinc-800">
                  <li>• 2 documentary-style founder interview sessions</li>
                  <li>• Complete Founder Neural Mapping report</li>
                  <li>• Organizational intelligence diagnostics</li>
                  <li>• NiyamHR onboarding for teams</li>
                  <li>• Pettai content workflow setup</li>
                  <li>• 30 strategic content assets</li>
                  <li>• LinkedIn + YouTube distribution planning</li>
                  <li>• Employer branding & GTM consultation</li>
                  <li>• 60-day strategic engagement</li>
                </ul>
              </div>
              <a href={mailto('Founder Neural OS Track — Engagement Brief')} className="mt-10 bg-gradient-to-r from-pink-500 via-orange-400 to-cyan-400 text-black px-6 py-3 rounded-2xl font-semibold hover:opacity-90 transition text-center">
                Schedule Consultation
              </a>
            </div>

            {/* Enterprise */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8 flex flex-col">
              <div>
                <div className="text-xs uppercase tracking-widest text-pink-400 mb-3">Institutional Track</div>
                <h3 className="text-3xl font-semibold">Institutional Intelligence Partnership</h3>
                <p className="mt-4 text-zinc-400 leading-relaxed">
                  A 6-month institutional intelligence partnership for enterprises seeking long-term organizational alignment, leadership visibility, and AI-assisted workforce systems.
                </p>
                <div className="mt-8 text-xl font-semibold text-zinc-300">
                  Investment: On Request
                </div>
                <ul className="mt-8 space-y-4 text-zinc-300">
                  <li>• Quarterly founder documentary series</li>
                  <li>• Executive leadership neural mapping</li>
                  <li>• Full organizational diagnostics</li>
                  <li>• AI employee mentoring deployment</li>
                  <li>• Internal communication architecture</li>
                  <li>• Monthly strategic advisory sessions</li>
                  <li>• Long-term employer branding systems</li>
                  <li>• Multi-platform visibility management</li>
                  <li>• 6-month engagement partnership</li>
                </ul>
              </div>
              <a href={mailto('Institutional Intelligence Partnership — Engagement Brief')} className="mt-10 border border-zinc-600 px-6 py-3 rounded-2xl hover:bg-zinc-800 transition text-center">
                Request Engagement Brief
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-8 md:px-20 py-24 text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-5xl font-bold leading-tight">
            Building the Intelligence Layer Behind Modern Organizations
          </h2>
          <p className="mt-8 text-xl text-zinc-400 leading-relaxed">
            Founder Neural OS combines strategic media, organizational intelligence, AI mentoring systems, and founder behavioral mapping to help visionary companies scale with clarity, culture, and alignment.
          </p>
          <a href={mailto('Founder Consultation Request')} className="inline-block mt-10 bg-white text-black px-8 py-4 rounded-2xl font-semibold text-lg hover:bg-zinc-200 transition">
            Schedule Founder Consultation
          </a>
        </div>
      </section>

      {/* Transformation Section */}
      <section id="transformation" className="px-8 md:px-20 py-24 bg-zinc-950 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-5xl mx-auto">
            <h2 className="text-5xl font-bold leading-tight">
              Transforming Founder-Led Organizations Through Intelligence, Alignment & Visibility
            </h2>
            <p className="mt-8 text-xl text-zinc-400 leading-relaxed">
              Founder Neural OS is designed to help organizations evolve beyond fragmented branding, disconnected teams, operational chaos, and inconsistent communication systems.
            </p>
            <p className="mt-6 text-lg text-zinc-500 leading-relaxed">
              By combining strategic founder interviews, AI-powered workforce mentoring, organizational diagnostics, GTM storytelling, and visibility architecture, we help businesses create stronger alignment between leadership, teams, customers, and long-term institutional growth.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-20">
            <div className="bg-black border border-zinc-800 rounded-3xl p-10">
              <h3 className="text-3xl font-semibold mb-6">Startups</h3>
              <p className="text-zinc-400 leading-relaxed">
                Early-stage founders often struggle with positioning clarity, hiring alignment, GTM consistency, and founder dependency. Founder Neural OS helps startups build visibility, culture systems, strategic communication, and scalable founder narratives from the beginning.
              </p>
              <ul className="mt-8 space-y-4 text-zinc-300">
                <li>• Founder positioning</li>
                <li>• GTM storytelling systems</li>
                <li>• Team alignment frameworks</li>
                <li>• AI mentoring workflows</li>
                <li>• Investor & employer visibility</li>
              </ul>
            </div>

            <div className="bg-black border border-zinc-800 rounded-3xl p-10">
              <h3 className="text-3xl font-semibold mb-6">SMEs & Growing Companies</h3>
              <p className="text-zinc-400 leading-relaxed">
                As companies scale, operational drift, communication gaps, founder overload, and inconsistent customer experience become major bottlenecks. We help organizations create stronger systems, leadership alignment, and strategic media infrastructure.
              </p>
              <ul className="mt-8 space-y-4 text-zinc-300">
                <li>• Organizational diagnostics</li>
                <li>• Founder neural mapping</li>
                <li>• Workforce alignment systems</li>
                <li>• Strategic visibility architecture</li>
                <li>• Employer branding & retention</li>
              </ul>
            </div>

            <div className="bg-black border border-zinc-800 rounded-3xl p-10">
              <h3 className="text-3xl font-semibold mb-6">Large Enterprises</h3>
              <p className="text-zinc-400 leading-relaxed">
                Established organizations require long-term institutional storytelling, leadership visibility, AI-driven workforce systems, and stronger communication alignment across functions. Founder Neural OS helps enterprises modernize culture and organizational intelligence.
              </p>
              <ul className="mt-8 space-y-4 text-zinc-300">
                <li>• Executive visibility systems</li>
                <li>• AI workforce mentoring</li>
                <li>• Leadership communication architecture</li>
                <li>• Institutional storytelling</li>
                <li>• Long-term strategic advisory</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-cyan-500/10 bg-gradient-to-b from-black to-zinc-950 px-6 md:px-16 py-20">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-12">
          <div>
            <h3 className="text-2xl font-bold tracking-tight"><span className="text-white">FOUNDER</span> <span className="bg-gradient-to-r from-blue-400 to-indigo-500 bg-clip-text text-transparent">NEURAL</span> <span className="bg-gradient-to-r from-pink-500 to-red-500 bg-clip-text text-transparent">OS</span></h3>
            <p className="mt-4 text-zinc-400 leading-relaxed">
              The umbrella intelligence layer for founder-led organizations. NiyamHR (workforce intelligence) and Pettai (brand visibility) are dedicated product systems within this ecosystem.
            </p>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Products</h4>
            <ul className="space-y-3 text-zinc-400">
              <li>
                <a href="https://niyamhr.in" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-300 transition duration-300">
                  NiyamHR
                </a>
              </li>
              <li>
                <a href="https://pettaiapp.in" target="_blank" rel="noopener noreferrer" className="hover:text-cyan-300 transition duration-300">
                  Pettai App
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Legal</h4>
            <ul className="space-y-3 text-zinc-400">
              <li><button onClick={() => setCurrentPage('/privacy-policy')} className="hover:text-cyan-300 transition duration-300">Privacy Policy</button></li>
              <li><button onClick={() => setCurrentPage('/terms-of-service')} className="hover:text-cyan-300 transition duration-300">Terms of Service</button></li>
              <li><button onClick={() => setCurrentPage('/ai-ethics-policy')} className="hover:text-cyan-300 transition duration-300">AI Ethics Policy</button></li>
              <li><button onClick={() => setCurrentPage('/content-usage-policy')} className="hover:text-cyan-300 transition duration-300">Content Usage Policy</button></li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Contact</h4>
            <ul className="space-y-3 text-zinc-400">
              <li>
                <a href={mailto('Founder Neural OS — Inquiry')} className="hover:text-cyan-300 transition duration-300">
                  contact@founderneuralos.com
                </a>
              </li>
              <li>Global Founder Intelligence Ecosystem</li>
              <li>Strategic Founder Consultations</li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-zinc-800 flex flex-col md:flex-row items-center justify-between text-zinc-500 text-sm gap-4">
          <p>© 2026 Founder Neural OS. All rights reserved.</p>
          <div className="flex gap-6">
            <button onClick={() => setCurrentPage('/privacy-policy')} className="hover:text-cyan-300 transition duration-300">Privacy</button>
            <button onClick={() => setCurrentPage('/terms-of-service')} className="hover:text-cyan-300 transition duration-300">Terms</button>
            <button onClick={() => setCurrentPage('/ai-ethics-policy')} className="hover:text-cyan-300 transition duration-300">Compliance</button>
          </div>
        </div>
      </footer>
    </div>
  );
}
